import { useContext, useEffect, useMemo, useRef } from "react";
import { CREATE_ACCOUNT_ERROR_DEFAULT_TITLE } from "@shared/features/onboarding/constants";
import {
  AddClientResponse,
  LanguageNameSpaces,
  OpenMessage,
} from "@shared/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { sharedLib } from "@shared/services/shared.service";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { getAccountFullName } from "@shared/features/portfolio/utils";
import { UrlQuickConnectQueryData } from "@shared/features/onboarding/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useUrlQuickConnectProps {
  queryData: UrlQuickConnectQueryData;
  openMessage: OpenMessage;
  onSubmitSuccess?: () => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  closeDrawer: () => void;
  isOnboarding?: boolean;
  isMobileApp?: boolean;
}

const useUrlQuickConnect = ({
  queryData,
  openMessage,
  onSubmitSuccess,
  getSuccessMessage,
  getErrorNotification,
  closeDrawer,
  isOnboarding,
  isMobileApp,
}: useUrlQuickConnectProps) => {
  const handledSuccess = useRef<string | undefined>(undefined);
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { isExternalConnectAccount, token: externalInviteToken } =
    useContext(ExternalConnectAccountContext) || {};
  const {
    createAccountDrawerOpen,
    setSelectedSubExchanges,
    setAccountName,
    getSelectedSubExchangesData,
    aggExchanges,
    setCreatedAccounts,
  } = useContext(AccountFormContext)!;
  const { statusCode, failMessage, accountBaseName, exchangesList, exchange } =
    queryData;

  const { refetch: refetchAccountsList } =
    sharedLib.sharedApi.configuration.useGetClientsListQuery(undefined, {
      skip: isExternalConnectAccount,
    });

  const [finalizeAccountInvite] =
    sharedLib.sharedApi.configuration.useFinalizeAccountInviteMutation();

  const decodedQueryExchanges = exchangesList
    ? decodeURIComponent(exchangesList)
    : undefined;
  const exchangesArray = useMemo(
    () => decodedQueryExchanges?.replace(/[\[\]']+/g, "").split(", "),
    [decodedQueryExchanges]
  );

  const subExchangesData = useMemo(
    () => (exchange ? aggExchanges[exchange] : undefined),
    [aggExchanges, exchange]
  );

  const accountsData = useMemo(() => {
    if (!exchangesArray || !subExchangesData) return;
    return exchangesArray?.map((subExchange) => {
      const subExchangeData = subExchangesData?.find(
        (exchange) => exchange.exchange === subExchange
      );
      return {
        exchange: subExchange,
        client_name: getAccountFullName(accountBaseName || "", subExchangeData),
      };
    });
  }, [accountBaseName, exchangesArray, subExchangesData]);

  const onSuccess = () => {
    closeDrawer();
    onSubmitSuccess?.();
    if (isExternalConnectAccount) {
      setCreatedAccounts(accountsData);
      if (externalInviteToken) {
        finalizeAccountInvite({ authToken: externalInviteToken }).unwrap();
      }
    } else {
      refetchAccountsList();
      if (exchangesArray) {
        getSuccessMessage?.(undefined, exchangesArray);
      }
    }
  };

  useEffect(() => {
    if (statusCode !== "200") {
      handledSuccess.current = undefined;
      return;
    }
    const callbackKey = JSON.stringify([
      exchange,
      accountBaseName,
      exchangesList,
      externalInviteToken,
    ]);
    if (
      handledSuccess.current !== callbackKey &&
      statusCode === "200" &&
      (createAccountDrawerOpen || isOnboarding || isMobileApp) &&
      (!isExternalConnectAccount || accountsData)
    ) {
      handledSuccess.current = callbackKey;
      onSuccess();
    }
  }, [
    statusCode,
    exchange,
    accountBaseName,
    exchangesList,
    createAccountDrawerOpen,
    isOnboarding,
    isMobileApp,
    isExternalConnectAccount,
    accountsData,
    externalInviteToken,
    exchangesArray,
  ]);

  useEffect(() => {
    if (statusCode === "400") {
      if (failMessage) {
        getErrorNotification?.(
          failMessage,
          t(CREATE_ACCOUNT_ERROR_DEFAULT_TITLE)
        );
      } else {
        openMessage("Something went wrong, please try again later", "error");
      }
    }
  }, [statusCode, failMessage]);

  useEffect(() => {
    if (statusCode === "400") {
      if (exchangesArray) {
        const subExchanges = getSelectedSubExchangesData(exchangesArray);
        if (subExchanges?.length) setSelectedSubExchanges(subExchanges);
      }
      if (accountBaseName) {
        setAccountName(accountBaseName);
      }
    }
  }, [
    exchangesArray,
    statusCode,
    accountBaseName,
    getSelectedSubExchangesData,
  ]);
};

export default useUrlQuickConnect;
